// react
import { SyntheticEvent, useCallback, useContext, useState } from 'react';

// types
import { Document, Entity, Loan, Pagination, Project, User } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextProject, ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// router
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

// mui
import { Box, Grid, Paper } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';

// ui
import { BlockchainHash } from '../../ui/BlockchainHash/BlockchainHash';
import { BtnExpand } from '../../ui/BtnExpand/BtnExpand';
import { BtnMenu } from '../../ui/BtnMenu/BtnMenu';
import { ChipStatus } from '../../ui/ChipStatus/ChipStatus';
import { ChipTraceability } from '../../ui/ChipTraceability/ChipTraceability';
import { ChipUtilization } from '../../ui/ChipUtilization/ChipUtilization';
import { entityIconTypes, IconCircle, IconCircleProps } from '../../ui/IconCircle/IconCircle';
import { MenuItemProps } from '../../ui/Menu/Menu';
import { RowKeyValue } from '../../ui/RowKeyValue/RowKeyValue';
import { Tabs } from '../../ui/Tabs/Tabs';
import { TypographyCaption } from '../../ui/TypographyCaption/TypographyCaption';
import { UserAvatarModified } from '../../ui/UserAvatarModified/UserAvatarModified';

// links
import { LinkEntity } from '../../links/LinkEntity/LinkEntity';

// icons
import {
	ArrowCircleRightOutlined as IconArrowCircleRight,
	InsertDriveFileOutlined as IconInsertDriveFile,
	ModeEditOutlined as IconModeEdit,
	DoDisturbOutlined as IconDoDisturb,
	PowerSettingsNewOutlined as IconPowerSettingsNew,
} from '@mui/icons-material';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// theme
import { theme } from '../../../theme';

// dialogs
import { DialogEditEntity } from '../../dialogs/DialogEditEntity/DialogEditEntity';
import { DialogUpdateEntityStatus } from '../../dialogs/DialogUpdateEntityStatus/DialogUpdateEntityStatus';
import { DialogUploadDocsEntity } from '../../dialogs/DialogUploadDocsEntity/DialogUploadDocsEntity';

// tables
import { TableBasicUsers } from '../../tables/basic/TableBasicUsers/TableBasicUsers';
import { TableBasicFinancing } from '../../tables/basic/TableBasicFinancing/TableBasicFinancing';
import { TableBasicDocs } from '../../tables/basic/TableBasicDocs/TableBasicDocs';

// utils
import { defaultPagination } from '../../../utils/defaults';
import { translateType } from '../../../utils/translations';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export interface CardEntityProps {
	entity: Entity;
	project: Project;
	refetchEntities?: () => void;
}

export const CardEntity = ({ entity, project, refetchEntities }: CardEntityProps) => {
	// hooks
	const { t } = useTranslation();

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [documents, setDocuments] = useState<Document[]>([]);
	const [isExpanded, setIsExpanded] = useState(false);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [paginationDocuments, setPaginationDocuments] = useState<Pagination>(defaultPagination);
	const [searchParams, setSearchParams] = useSearchParams();
	const [tabValue, setTabValue] = useState('financing');
	const [users, setUsers] = useState<User[]>(entity.users ? (entity.users as User[]) : []);

	// context
	const { isOnboarded: isProjectOnboarded } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogEntity = searchParams.get('dialogEntity');

	// fetch loans
	const { isLoading: isLoadingLoans } = useFetch({
		isEnabled: Boolean(
			entity && project && isProjectOnboarded && isExpanded && tabValue === 'financing'
		),
		url: `${process.env.REACT_APP_API_URL}/loans?entity=${entity.id}&project=${project?.id}&fields=accounts,code,entities,financier,id&populate=accounts,entities`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setLoans(res.data);
			}
		},
	});

	// fetch users
	const { isLoading: isLoadingUsers } = useFetch({
		isEnabled: Boolean(entity && isProjectOnboarded && isExpanded && tabValue === 'users'),
		url: `${process.env.REACT_APP_API_URL}/users?projects=${project?.id}&entities=${entity.id}&populate=country`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setUsers(res.data);
			}
		},
	});

	// fetch documents
	const urlFetchDocs = `${process.env.REACT_APP_API_URL}/documents?entities=${entity.id}&limit=10&page=1&fields=created,notarizationHash,id,name,type,user&populate=type,user`;

	const { isLoading: isLoadingDocs, refetch: refetchDocuments } = useFetch({
		isEnabled: Boolean(entity && isProjectOnboarded && isExpanded && tabValue === 'documents'),
		url: urlFetchDocs,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDocuments(res.data);
			}

			if (res.pagination) {
				setPaginationDocuments(res.pagination);
			}
		},
	});

	// handlers
	const handleToggleCard = () => {
		setIsExpanded(!isExpanded);
	};

	const handleOpenDialogUpdateEntity = useCallback(() => {
		searchParams.set('dialog', 'updateEntityStatus');
		searchParams.set('dialogEntity', String(entity.id));
		setSearchParams(searchParams);
	}, [entity, searchParams, setSearchParams]);

	const handleOpenDialogEditEntity = useCallback(() => {
		searchParams.set('dialog', 'editEntity');
		searchParams.set('dialogEntity', String(entity.id));
		setSearchParams(searchParams);
	}, [entity, searchParams, setSearchParams]);

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogEntity');
		setSearchParams(searchParams);
	};

	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	// vars
	const btnMenuItems: MenuItemProps[] = [
		{
			children: t('seeDetails'),
			component: RouterLink,
			icon: <IconArrowCircleRight />,
			to: `/project/${project?.idWbg}/entity/${entity.id}`,
		},
	];

	if (userProjectPermissions?.document?.create) {
		btnMenuItems.push({
			children: t('uploadDocuments'),
			icon: <IconInsertDriveFile />,
			onClick: () => {
				searchParams.set('dialog', 'uploadDocsEntity');
				searchParams.set('dialogEntity', String(entity.id));
				setSearchParams(searchParams);
			},
		});
	}

	if (userProjectPermissions?.entity?.create) {
		btnMenuItems.push({
			children: t('editEntity'),
			icon: <IconModeEdit />,
			onClick: handleOpenDialogEditEntity,
		});
	}

	if (userProjectPermissions?.entity?.update) {
		let btnLabel = t('deactivateEntity');
		let btnIcon = <IconDoDisturb color="error" />;

		if (entity.wallet?.status === 'INACTIVE') {
			btnLabel = t('activateEntity');
			btnIcon = <IconPowerSettingsNew color="success" />;
		}

		btnMenuItems.push({
			children: btnLabel,
			icon: btnIcon,
			onClick: handleOpenDialogUpdateEntity,
		});
	}

	const data = loans.map((loan) => {
		const entityActive = loan.entities.find((e: Entity) => e.id === entity.id);
		return {
			...loan,
			parent: entityActive ? entityActive.parent : null,
		};
	});

	const tabs = [
		{
			label: t('financing'),
			value: 'financing',
		},
	];

	if (userProjectPermissions?.user?.read) {
		tabs.push({
			label: t('users'),
			value: 'users',
		});
	}

	if (userProjectPermissions?.document?.read) {
		tabs.push({
			label: t('documents'),
			value: 'documents',
		});
	}

	const IconCircleProps: IconCircleProps = {
		TooltipProps: {
			title: translateType(entity.type, t),
		},
		type: entityIconTypes[entity.type] || 'entity',
	};

	return (
		<>
			<Paper
				sx={(theme) => {
					const borderColor = isExpanded ? theme.palette.brand.blue[500] : 'transparent';
					return {
						border: `1px ${borderColor} solid`,
						position: 'relative',
					};
				}}>
				<Box sx={{ padding: theme.spacing(2) }}>
					<Grid container alignItems={'center'} spacing={{ xs: 1, md: 2 }}>
						<Grid item xs={12} lg={3}>
							<Box
								sx={{
									margin: theme.spacing(0, 0, 2, 0),
									[theme.breakpoints.up('lg')]: {
										margin: 0,
									},
								}}>
								<Grid alignItems="center" container spacing={2} wrap="nowrap">
									<Grid item>
										<IconCircle {...IconCircleProps} />
									</Grid>
									<Grid item>
										<TypographyCaption id={entity.id} />
										<LinkEntity entity={entity} />
									</Grid>
								</Grid>
							</Box>
						</Grid>
						<Grid item xs={12} lg={2}>
							<RowKeyValue
								label={t('wallet')}
								value={
									entity.wallet?.address ? <BlockchainHash hash={entity.wallet.address} /> : t('na')
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1}>
							<RowKeyValue
								label={t('status')}
								value={
									<ChipStatus
										status={isProjectOnboarded ? entity.wallet?.status || 'ACTIVE' : 'PENDING'}
									/>
								}
							/>
						</Grid>
						{featureFlags['UTILIZATION'] && (
							<Grid item xs={12} lg={1.5}>
								<RowKeyValue
									label={t('utilization')}
									value={<ChipUtilization utilization={entity.utilization} />}
								/>
							</Grid>
						)}
						<Grid item xs={12} lg={1}>
							<RowKeyValue
								label={t('transactionsEvidenced')}
								value={
									<ChipTraceability traceability={entity.traceability || 0} type="TRANSACTIONS" />
								}
							/>
						</Grid>
						{featureFlags['FINANCIAL_TRACEABILITY'] && (
							<Grid item xs={12} lg={1}>
								<RowKeyValue
									label={t('financialValueEvidenced')}
									value={
										<ChipTraceability traceability={entity.traceability || 0} type="FINANCIAL" />
									}
								/>
							</Grid>
						)}
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('updated')}
								value={
									<UserAvatarModified
										entity={entity}
										activityTypes={['ENTITY_UPDATED', 'ENTITY_CREATED']}
									/>
								}
							/>
						</Grid>

						<Grid item xs={12} lg={true}>
							<Grid alignItems="center" container justifyContent={{ lg: 'flex-end' }} spacing={1}>
								{isProjectOnboarded && btnMenuItems.length > 0 && (
									<Grid
										item
										sx={{
											position: 'absolute',
											top: 0,
											right: theme.spacing(1),
											[theme.breakpoints.up('lg')]: {
												position: 'relative',
												top: 'auto',
												right: 'auto',
											},
										}}>
										<BtnMenu items={btnMenuItems} />
									</Grid>
								)}
								<Grid item>
									<BtnExpand
										isExpanded={isExpanded}
										label={isExpanded ? t('hideDetails') : t('seeDetails')}
										onClick={handleToggleCard}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Box>
				{isExpanded && (
					<TabContext value={tabValue}>
						<Tabs
							BoxProps={{
								sx: {
									marginTop: {
										lg: theme.spacing(3),
									},
								},
							}}
							hasBorder={false}
							onChange={handleChangeTab}
							tabs={tabs}
							value={tabValue}
						/>
						<TabPanel value="financing">
							<TableBasicFinancing data={data} isLoading={isLoadingLoans} />
						</TabPanel>
						<TabPanel value="users">
							<TableBasicUsers data={users} isLoading={isLoadingUsers} />
						</TabPanel>
						<TabPanel value="documents">
							<TableBasicDocs
								data={documents}
								isLoading={isLoadingDocs}
								pagination={paginationDocuments}
								refetchDocuments={refetchDocuments}
							/>
						</TabPanel>
					</TabContext>
				)}
			</Paper>
			<DialogUpdateEntityStatus
				entity={entity}
				isOpen={paramDialog === 'updateEntityStatus' && paramDialogEntity === String(entity.id)}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchEntities) {
						refetchEntities();
					}
				}}
			/>
			<DialogEditEntity
				entity={entity}
				isOpen={paramDialog === 'editEntity' && paramDialogEntity === String(entity.id)}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchEntities) {
						refetchEntities();
					}
				}}
			/>
			<DialogUploadDocsEntity
				entity={entity}
				isOpen={paramDialog === 'uploadDocsEntity' && paramDialogEntity === String(entity.id)}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchEntities) {
						refetchEntities();
					}
				}}
			/>
		</>
	);
};
